import React from 'react';
import { getAuthToken } from '../services/auth';
import { getBaseUrl } from '../services/baseUrl';

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_PANEL = '#FBF8F2';
const BRAND_CARD = '#FFFDF9';
const BRAND_BORDER = '#DDD4C8';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_ACCENT_BG = 'rgba(36, 117, 122, 0.1)';
const BRAND_DANGER_BG = '#FDEDEC';
const BRAND_DANGER_TEXT = '#A63B32';

export type TaskDeviceExpertContextOption = {
  key: string;
  label: string;
  detail?: string;
  prompt?: string;
};

export type TaskDeviceExpertParentOption = {
  id: string;
  sequence?: number | string;
  label: string;
  contextOptions: TaskDeviceExpertContextOption[];
};

type TaskDeviceExpertMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
};

type TaskDeviceExpertPanelProps = {
  modelId?: string | null;
  parentOptions: TaskDeviceExpertParentOption[];
  taskLabel: string;
  parentLabel: string;
  contextLabel: string;
  title?: string;
  subtitle?: string;
  noParentLabel?: string;
  noContextLabel?: string;
  selectParentFirstLabel?: string;
  questionPlaceholder?: string;
  contextHint?: string;
  unavailableText?: string;
  emptyContextText?: string;
};

const cleanText = (value: unknown) => String(value || '').trim();

const formatDeviceExpertText = (value: string) =>
  cleanText(value)
    .split(/\n{2,}/)
    .map((paragraph, index) => (
      <p key={`${index}-${paragraph.slice(0, 12)}`} style={styles.messageParagraph}>
        {paragraph}
      </p>
    ));

const TaskDeviceExpertPanel: React.FC<TaskDeviceExpertPanelProps> = ({
  modelId,
  parentOptions,
  taskLabel,
  parentLabel,
  contextLabel,
  title = 'Device Expert',
  subtitle,
  noParentLabel,
  noContextLabel,
  selectParentFirstLabel,
  questionPlaceholder,
  contextHint,
  unavailableText,
  emptyContextText
}) => {
  const [open, setOpen] = React.useState(true);
  const [parentId, setParentId] = React.useState('');
  const [contextKey, setContextKey] = React.useState('');
  const [question, setQuestion] = React.useState('');
  const [messages, setMessages] = React.useState<TaskDeviceExpertMessage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [conversationId, setConversationId] = React.useState<string | null>(null);

  const selectedParent = React.useMemo(
    () => parentOptions.find(option => option.id === parentId) || null,
    [parentId, parentOptions]
  );
  const contextOptions = selectedParent?.contextOptions || [];
  const selectedContext = React.useMemo(
    () => contextOptions.find(option => option.key === contextKey) || null,
    [contextKey, contextOptions]
  );

  React.useEffect(() => {
    if (parentId && !selectedParent) {
      setParentId('');
      setContextKey('');
    }
  }, [parentId, selectedParent]);

  React.useEffect(() => {
    if (contextKey && !contextOptions.some(option => option.key === contextKey)) {
      setContextKey('');
    }
  }, [contextKey, contextOptions]);

  const askDeviceExpert = React.useCallback(async () => {
    if (!modelId) {
      setError(unavailableText || `This ${taskLabel} has no device-model context yet, so Device Expert is unavailable.`);
      return;
    }

    const customQuestion = question.trim();
    const quickQuestion = selectedContext
      ? `How do I carry out ${taskLabel} ${contextLabel.toLowerCase()} ${selectedContext.label} safely and correctly?`
      : '';
    const technicianQuestion = customQuestion || quickQuestion;

    if (!technicianQuestion) {
      setError(`Select a ${contextLabel.toLowerCase()} or enter a question.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const base = await getBaseUrl();
      const token = getAuthToken();
      const condensedHistory = messages.slice(-6).map(message => ({
        role: message.role,
        content: message.content
      }));
      const parentContext = selectedParent
        ? `${parentLabel} ${selectedParent.sequence || ''}: ${selectedParent.label}`.replace(/\s+/g, ' ').trim()
        : '';
      const childContext =
        selectedContext && selectedParent
          ? `${contextLabel}: ${selectedContext.prompt || selectedContext.detail || selectedContext.label}`.trim()
          : '';
      const focusContext = [parentContext, childContext].filter(Boolean).join(' | ');
      const formattedQuestion = focusContext
        ? [`Focus step: ${focusContext}`, `Technician question: ${technicianQuestion}`].join('\n')
        : technicianQuestion;

      const response = await fetch(`${base}/api/knowledge-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          modelId,
          question: formattedQuestion,
          history: condensedHistory,
          conversationId,
          includeFundamentals: false,
          includeImages: false,
          responseLanguage: 'EN'
        })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to get Device Expert guidance.');
      }

      const now = new Date().toISOString();
      const assistantTimestamp = payload?.timestamp || now;
      setMessages(current => [
        ...current,
        {
          id: `user-${now}`,
          role: 'user',
          content: technicianQuestion,
          timestamp: now
        },
        {
          id: `assistant-${assistantTimestamp}`,
          role: 'assistant',
          content: cleanText(payload?.answer),
          timestamp: assistantTimestamp
        }
      ]);
      setConversationId(payload?.conversationId || conversationId || null);
      setQuestion('');
    } catch (err) {
      console.error(`${taskLabel} Device Expert failed`, err);
      setError(err instanceof Error ? err.message : 'Failed to get Device Expert guidance.');
    } finally {
      setLoading(false);
    }
  }, [
    contextLabel,
    contextOptions,
    conversationId,
    messages,
    modelId,
    parentLabel,
    question,
    selectedContext,
    selectedParent,
    taskLabel,
    unavailableText
  ]);

  const canAsk = Boolean(modelId) && !loading && Boolean(question.trim() || selectedContext);
  const resolvedSubtitle =
    subtitle || `Ask about a selected ${taskLabel} step for contextual help, or ask an open device question.`;
  const resolvedHint = modelId
    ? contextHint ||
      `Choosing a ${contextLabel.toLowerCase()} adds that task context. With no step selected, your question is treated as an open Device Expert ask.`
    : unavailableText || `This ${taskLabel} has no device-model context yet, so Device Expert is unavailable.`;

  return (
    <section style={styles.section}>
      <div style={styles.header}>
        <div style={styles.headerCopy}>
          <div style={styles.title}>{title}</div>
          <div style={styles.subtitle}>{resolvedSubtitle}</div>
        </div>
        <button type="button" style={styles.secondaryButton} onClick={() => setOpen(current => !current)}>
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open ? (
        <>
          <div style={styles.grid}>
            <label style={styles.field}>
              <span style={styles.label}>{parentLabel}</span>
              <select
                style={styles.select}
                value={parentId}
                onChange={event => {
                  setParentId(event.target.value);
                  setContextKey('');
                  setError(null);
                }}
                disabled={loading || parentOptions.length === 0}
              >
                <option value="">{noParentLabel || `No specific ${parentLabel.toLowerCase()} selected`}</option>
                {parentOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {[option.sequence, option.label].filter(Boolean).join('. ')}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.field}>
              <span style={styles.label}>{contextLabel}</span>
              <select
                style={styles.select}
                value={contextKey}
                onChange={event => {
                  setContextKey(event.target.value);
                  setError(null);
                }}
                disabled={loading || !selectedParent}
              >
                <option value="">
                  {selectedParent
                    ? noContextLabel || `No ${contextLabel.toLowerCase()} selected`
                    : selectParentFirstLabel || `Select a ${parentLabel.toLowerCase()} first`}
                </option>
                {contextOptions.map(option => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label style={styles.field}>
            <span style={styles.label}>Question</span>
            <input
              style={styles.input}
              value={question}
              onChange={event => {
                setQuestion(event.target.value);
                setError(null);
              }}
              placeholder={
                questionPlaceholder ||
                `Ask a ${taskLabel} question, or leave blank to get more detail on the selected ${contextLabel.toLowerCase()}.`
              }
              disabled={loading || !modelId}
            />
            <span style={styles.hint}>{parentOptions.length === 0 ? emptyContextText || resolvedHint : resolvedHint}</span>
          </label>
          <div style={styles.actions}>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                ...(!canAsk ? styles.disabledButton : {})
              }}
              onClick={askDeviceExpert}
              disabled={!canAsk}
            >
              {loading ? 'Asking...' : 'Ask Device Expert'}
            </button>
            <button
              type="button"
              style={{
                ...styles.secondaryButton,
                ...(loading ? styles.disabledButton : {})
              }}
              onClick={() => {
                setQuestion('');
                setMessages([]);
                setError(null);
                setConversationId(null);
              }}
              disabled={loading}
            >
              Clear
            </button>
          </div>
          {error ? <div style={styles.error}>{error}</div> : null}
          {messages.length > 0 ? (
            <div style={styles.messages}>
              {messages.map(message => (
                <div
                  key={message.id}
                  style={{
                    ...styles.message,
                    ...(message.role === 'assistant' ? styles.assistantMessage : styles.userMessage)
                  }}
                >
                  <strong>{message.role === 'assistant' ? 'Assistant' : 'You'}:</strong>{' '}
                  {message.role === 'assistant' ? formatDeviceExpertText(message.content) : message.content}
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: BRAND_PANEL,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '1rem',
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    boxShadow: '0 16px 32px -28px rgba(18, 63, 67, 0.28)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  headerCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem'
  },
  title: {
    color: BRAND_TEXT,
    fontWeight: 800,
    fontSize: '1.05rem'
  },
  subtitle: {
    color: BRAND_MUTED,
    lineHeight: 1.45,
    fontSize: '0.92rem'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))',
    gap: '0.9rem'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem'
  },
  label: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 800
  },
  select: {
    width: '100%',
    minHeight: '2.8rem',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    padding: '0.72rem 0.9rem',
    fontSize: '0.95rem',
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  input: {
    width: '100%',
    minHeight: '2.8rem',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    padding: '0.72rem 0.9rem',
    fontSize: '0.95rem',
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  hint: {
    color: BRAND_MUTED,
    fontSize: '0.85rem',
    lineHeight: 1.45
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  primaryButton: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 800,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)'
  },
  secondaryButton: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 800,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  disabledButton: {
    opacity: 0.65,
    cursor: 'not-allowed'
  },
  error: {
    borderRadius: '0.85rem',
    border: '1px solid #F4C8C5',
    background: BRAND_DANGER_BG,
    color: BRAND_DANGER_TEXT,
    padding: '0.85rem 0.95rem',
    fontWeight: 700
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  message: {
    borderRadius: '0.85rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.85rem 0.95rem',
    lineHeight: 1.5,
    color: BRAND_TEXT
  },
  assistantMessage: {
    background: '#FFFFFF'
  },
  userMessage: {
    background: BRAND_ACCENT_BG
  },
  messageParagraph: {
    margin: '0.35rem 0 0',
    whiteSpace: 'pre-wrap'
  }
};

export default TaskDeviceExpertPanel;
